import { TLocationContact, TRUCK_SIZE } from '@types';

export type LocationGroup = {
	id: number;
	name: string;
}

export enum LOCATION_TYPE {
	WAREHOUSE = 'WAREHOUSE',
	DISTRIBUTION_CENTRE = 'DISTRIBUTION_CENTRE',
	CROSS_DOCK = 'CROSS_DOCK',
	STORE = 'STORE',
}

export const getLocationTypeLabel = (locationType: LOCATION_TYPE): string => {
	switch (locationType) {
	case LOCATION_TYPE.WAREHOUSE:
		return 'Warehouse';
	case LOCATION_TYPE.DISTRIBUTION_CENTRE:
		return 'Distribution Centre';
	case LOCATION_TYPE.CROSS_DOCK:
		return 'Cross dock';
	case LOCATION_TYPE.STORE:
		return 'Store';
	default:
		return '';
	}
}

export enum DangerousGoodType {
	FLAMMABLE_SOLIDS = 'FLAMMABLE_SOLIDS',
	GASSES = 'GASSES',
	TOXIC_SUBSTANCES = 'TOXIC_SUBSTANCES',
	CORROSIVE_SUBSTANCES = 'CORROSIVE_SUBSTANCES',
	OXIDISING_AGENTS = 'OXIDISING_AGENTS',
	RADIOACTIVE_MATERIAL = 'RADIOACTIVE_MATERIAL',
	EXPLOSIVES = 'EXPLOSIVES',
	HAZARDOUS = 'HAZARDOUS',
	MISCELLANEOUS_DG = 'MISCELLANEOUS_DG'
}

export const getDangerousGoodTypeLabel = (dangerousGoodType: DangerousGoodType): string => {
	switch (dangerousGoodType) {
	case DangerousGoodType.FLAMMABLE_SOLIDS:
		return 'Flammable Solids';
	case DangerousGoodType.GASSES:
		return 'Gasses';
	case DangerousGoodType.TOXIC_SUBSTANCES:
		return 'Toxic Substances';
	case DangerousGoodType.CORROSIVE_SUBSTANCES:
		return 'Corrosive Substances';
	case DangerousGoodType.OXIDISING_AGENTS:
		return 'Oxidising Agents';
	case DangerousGoodType.RADIOACTIVE_MATERIAL:
		return 'Radioactive Material';
	case DangerousGoodType.EXPLOSIVES:
		return 'Explosives';
	case DangerousGoodType.HAZARDOUS:
		return 'Environmentally Hazardous Substances';
	case DangerousGoodType.MISCELLANEOUS_DG:
		return 'Miscellaneous Dangerous Goods';
	default:
		return 'Unknown';
	}
}
export enum PPEOption {
	SAFETY_SHOES = 'SAFETY_SHOES',
	SAFETY_GLASSES = 'SAFETY_GLASSES',
	HIGH_VISIBILITY_CLOTHING = 'HIGH_VISIBILITY_CLOTHING',
	LONG_SLEEVES = 'LONG_SLEEVES',
	HARD_HATS = 'HARD_HATS',
	HAZARD_SPILL_KITS = 'HAZARD_SPILL_KITS',
	LONG_PANTS = 'LONG_PANTS'
}


export const getPPEOptionLabel = (ppeOption: PPEOption): string => {
	switch (ppeOption) {
	case PPEOption.SAFETY_SHOES:
		return 'Safety Shoes/Boots';
	case PPEOption.SAFETY_GLASSES:
		return 'Safety Glasses/Goggles';
	case PPEOption.HIGH_VISIBILITY_CLOTHING:
		return 'High-Visibility Clothing';
	case PPEOption.LONG_SLEEVES:
		return 'Protective high visibility Sleeves';
	case PPEOption.LONG_PANTS:
		return 'Protective Trousers or Leg Protection';
	case PPEOption.HARD_HATS:
		return 'Hard Hats';
	case PPEOption.HAZARD_SPILL_KITS:
		return 'Spill Response Kits or Chemical Spill Kits';
	default:
		return 'Unknown';
	}
}
export enum LoadingOptions {
	REAR_LOAD = 'REAR_LOAD',
	SIDE_LOAD = 'SIDE_LOAD',
	TOP_LOAD = 'TOP_LOAD',
	HAND_LOAD = 'HAND_LOAD',
	TAIL_GATE = 'TAIL_GATE',
	PALLET_JACK = 'PALLET_JACK',
	SPLIT_TRAILER = 'SPLIT_TRAILER',
	LOADING_EQUIPMENT = 'LOADING_EQUIPMENT'
}

export type LoadingOptionsKeys = keyof typeof LoadingOptions;

export type LocationOwnerShipper = {
	shipper_id: number;
	company_name: string;
	trade_name: string;
	business_name: string;
	external_id?: string;
}

export type LocationOwnerCarrier = {
	carrier_id: number;
	company_name: string;
	trade_name: string;
	external_id?: string;
}

export type LocationOwnersResponse = {
	shippers: LocationOwnerShipper[];
	carriers: LocationOwnerCarrier[];
}
export type LoadingOptionsMap = {
	[key in LoadingOptions]: boolean | null;
};
export enum LocationStep {
	DETAILS = 'details',
	LOADING = 'loadings',
	SHIPPER = 'shipper-details'
}

export type TimePeriod = {
	start: string;
	end: string;
}
export type OpeningTimes = {
	Monday?: TimePeriod | null;
	Tuesday?: TimePeriod | null;
	Wednesday?: TimePeriod | null;
	Thursday?: TimePeriod | null;
	Friday?: TimePeriod | null;
	Saturday?: TimePeriod | null;
	Sunday?: TimePeriod | null;
}
export type LocationFormState = {
	locationName: string;
	address: string;
	locationType: string[]
	openingTimes: OpeningTimes;
	blackoutPeriods: {
		startDate: string;
		endDate: string;
	}[];
	timeSlotRequired: boolean;
	timeSlotProcessInfo: string|null;
	defaultDriverInstructions: string;
	restrictedTrucks: {truckType: number, truckSize: number}[];
	ppeInductionRequired: boolean;
	ppeInductionNotes: string;
	ppeRequired: boolean;
	loadingOptions: LoadingOptionsMap;
	splitTrailerTruckSizes: TRUCK_SIZE[];
	dangerousGoodsAllowed: boolean | null;
	ppeList: string[];
	dangerousGoodsTypes: string[];
	dangerousGoodsHandling: string;
	ownerShipperId: number | null;
	ownerShipperName: string;
	ownerCarrierId: number | null;
	ownerCarrierName: string;
	chepTransfer: boolean;
	loscamTransfer: boolean;
	palletExchange: boolean;
	chepAccountNumber: string;
	loscamAccountNumber: string;
	locationGroup: LocationGroup | null;
}

export type TLocationShipperDocument = {
	name: string;
	object_type: string;
	path: string;
	id: number;
	document_id: number;
	created_at: string;
	external_id?: string;
	tempID: string;
	file: File;
}

export type TLocationShipperDetail = {
	shipper_name : string;
	location_alias: string;
	instructions: string,
	is_hypercare: boolean,
	shipper_id: number | null;
	id: number | null;
	location_group_id: number | null;
	location_group: LocationGroup | null;
	contacts: TLocationContact[];
	documents: Partial<TLocationShipperDocument>[]
}